(() => {
	// Any popover arrow on the page. Used for quick existence checks.
	const ARROW_SELECTOR = '[popover] .arrow';
	// Popovers that should receive dynamic arrow positioning.
	const TOOLTIP_SELECTOR = '[popover]:has(.arrow)';
	// Minimum distance from arrow center to popover edge.
	const EDGE_PADDING = 20;

	// Finds the trigger associated with this popover via the `popovertarget` attribute.
	function getTriggerForPopover(popover) {
		if (!popover || !popover.id) {
			return null;
		}

		return document.querySelector(`[popovertarget="${popover.id}"]`);
	}

	// Positions one arrow by measuring trigger + popover geometry in viewport space.
	function positionArrow(popover) {
		if (!popover) {
			return;
		}

		const arrow = popover.querySelector('.arrow');
		const trigger = getTriggerForPopover(popover);
		if (!arrow || !trigger) {
			return;
		}

		// Current on-screen rectangles after browser placement/fallbacks.
		const popoverRect = popover.getBoundingClientRect();
		const triggerRect = trigger.getBoundingClientRect();
		const triggerCenterX = triggerRect.left + (triggerRect.width / 2);

		// Align to trigger center, but keep the arrow from clipping outside the popover.
		const minLeft = EDGE_PADDING;
		const maxLeft = Math.max(popoverRect.width - EDGE_PADDING, EDGE_PADDING);
		const desiredLeft = triggerCenterX - popoverRect.left;
		const clampedLeft = Math.min(Math.max(desiredLeft, minLeft), maxLeft);

		// If the popover rendered below the trigger, CSS should dock arrow on top edge.
		// Otherwise CSS keeps the arrow on the bottom edge.
		const isBelowAnchor = popoverRect.top >= triggerRect.bottom;
		popover.classList.toggle('is-below-anchor', isBelowAnchor);

		// Expose horizontal arrow offset to CSS.
		popover.style.setProperty('--tooltip-arrow-left', `${clampedLeft}px`);
	}

	// Recompute arrow positions for all currently open tooltips.
	function positionOpenArrows() {
		const openPopovers = document.querySelectorAll(`${TOOLTIP_SELECTOR}:popover-open`);
		openPopovers.forEach((popover) => positionArrow(popover));
	}

	// Collapse bursty events (scroll/resize/toggle) into one paint-frame update.
	let rafId = null;
	function scheduleReposition() {
		if (rafId !== null) {
			return;
		}

		rafId = window.requestAnimationFrame(() => {
			rafId = null;
			positionOpenArrows();
		});
	}

	// On trigger activation, wait a few frames for popover open + anchor settlement,
	// then position the arrow once the popover is actually open.
	function schedulePopoverOpenPosition(popoverId, remainingFrames = 6) {
		if (!popoverId || remainingFrames <= 0) {
			return;
		}

		window.requestAnimationFrame(() => {
			const popover = document.getElementById(popoverId);
			if (!popover || !popover.matches(TOOLTIP_SELECTOR)) {
				return;
			}

			if (popover.matches(':popover-open')) {
				positionArrow(popover);
				return;
			}

			schedulePopoverOpenPosition(popoverId, remainingFrames - 1);
		});
	}

	// DOMContentLoaded ensures the DOM is fully parsed before querying for arrows
	// or registering listeners. This guards against silent no-ops if the script
	// loads before the relevant elements exist (i.e. without a `defer` attribute).
	document.addEventListener('DOMContentLoaded', () => {
		// Skip global listeners on pages where this behavior is not used.
		if (!document.querySelector(ARROW_SELECTOR)) {
			return;
		}

		// Also reposition on trigger click so first-open mobile placement is correct
		// even before any scroll/resize events occur.
		document.addEventListener('click', (event) => {
			const trigger = event.target.closest('[popovertarget]');
			if (!(trigger instanceof HTMLElement)) {
				return;
			}

			const popoverId = trigger.getAttribute('popovertarget');
			schedulePopoverOpenPosition(popoverId);
		});

		// Keep arrow alignment in sync during viewport/layout movement.
		// passive: true lets the browser scroll without waiting on this handler.
		window.addEventListener('resize', scheduleReposition);
		window.addEventListener('scroll', scheduleReposition, { capture: true, passive: true });
	});
})();
